// src/pages/OrdersProfitSummaryPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";
import DateRange from "../components/DateRange";
import SummaryCard from "../components/SummaryCard";


/** ---------- Types (ตาม /orders-summary) ---------- */
type Row = {
  billNo: string;
  paymentMethod: string | null;
  saleChannel: string | null;
  recDateBE: string | null;
  recTime: string | null;
  totalItems: number;
  orderTotal: number;
  packageFee: number;
  shippingFee: number;
  discountAmount: number | null;
  afterDiscount: number | null;
  productCode: string | null;
  productName: string | null;
  qty: number | null;
  unitPrice: number | null;
  tiktokNetPayout: number | null;
  unitCost: number | null;
  profit: number | null;
  orderCreated: string;
};

type OrderItem = {
  productCode: string;
  productName: string;
  qty: number;
  unitPrice: number;
  effectiveUnitPrice: number;
  unitCost: number;
  lineTotal: number;
  lineProfit: number;
};

type Order = {
  billNo: string;
  paymentMethod: string | null;
  saleChannel: string | null;
  recDateBE: string | null;
  recTime: string | null;
  totalItems: number;
  orderTotal: number;
  packageFee: number;
  shippingFee: number;
  discountAmount: number;
  afterDiscount: number;
  createdAt: string;
  items: OrderItem[];
  billProfit: number;
};

type SaleChannelOption = {
  id: number;
  code: string;
  label: string;
  is_active: boolean;
};

// ===== Types =====
type SettlementItem = {
  id: number;
  dateFrom: string; // "YYYY-MM-DD"
  dateTo: string; // "YYYY-MM-DD"
  salesCash: number | null;
  countedCash: number | null;
  salesScan: number | null;
  countedScan: number | null;
  updatedAt?: string | null;
};

type SettlementGetResp = { items: SettlementItem[] };
type SettlementPutReq = {
  dateFrom: string;
  dateTo: string;
  channel: "CASH" | "SCAN";
  salesAmount: number;
  countedAmount: number;
};
type SettlementPutResp = { ok: true };

type SettlementRowApi = {
  id: number;
  date_from: string;
  date_to: string;
  sales_cash: string | number | null;
  counted_cash: string | number | null;
  sales_scan: string | number | null;
  counted_scan: string | number | null;
  updated_at: string | null;
};

type SettlementGetRespApi = { items: SettlementRowApi[] };

function toNum(x: unknown): number | null {
  if (x == null) return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function mapSettlementRow(r: SettlementRowApi): SettlementItem {
  return {
    id: r.id,
    dateFrom: r.date_from,
    dateTo: r.date_to,
    salesCash: toNum(r.sales_cash),
    countedCash: toNum(r.counted_cash),
    salesScan: toNum(r.sales_scan),
    countedScan: toNum(r.counted_scan),
    updatedAt: r.updated_at,
  };
}



/** ---------- Helpers (ควรย้ายไป src/utils/* หากใช้ซ้ำ) ---------- */
const thb = (n: number): string => n.toLocaleString("th-TH", { style: "currency", currency: "THB" });

// dd/mm/yyyy (พ.ศ.) + time → Date(ค.ศ.)
// PMN-CRITICAL(/OrdersProfitSummaryPage): KPI date filters use AD Date objects, while recDateBE stores BE years; keep this BE->AD conversion aligned with OrdersProfitPage.
function beDMYToDate(dmy: string, time?: string): Date | null {
  const slashMatch = dmy?.match?.(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const isoMatch = dmy?.match?.(/^(\d{4})-(\d{2})-(\d{2})$/);

  let dd = 0;
  let mm = 0;
  let yyyyBE = 0;

  if (slashMatch) {
    dd = Number(slashMatch[1]);
    mm = Number(slashMatch[2]);
    yyyyBE = Number(slashMatch[3]);
  } else if (isoMatch) {
    yyyyBE = Number(isoMatch[1]);
    mm = Number(isoMatch[2]);
    dd = Number(isoMatch[3]);
  } else {
    return null;
  }

  const yyyyAD = yyyyBE - 543;
  const hhmmss = time && /^\d{2}:\d{2}(:\d{2})?$/.test(time) ? time : "00:00:00";
  return new Date(`${yyyyAD}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}T${hhmmss}`);
}

function toYMDLocal(dt: Date): string {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function resolveOrderDate(o: Order): Date | null {
  if (o.recDateBE) {
    const d = beDMYToDate(o.recDateBE, o.recTime ?? undefined);
    if (d && !Number.isNaN(d.getTime())) return d;
  }
  if (o.createdAt) {
    const d = new Date(o.createdAt);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function formatThaiDateTime(iso?: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString("th-TH", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** แปลง rows จาก /orders-summary → ออบเจ็กต์บิล พร้อมคำนวณกำไรต่อบิล */
// PMN-CRITICAL(/OrdersProfitSummaryPage): All KPI cards depend on this frontend profit formula; keep TikTok payout/discount/package/shipping rules in sync with OrdersProfitPage.
function rowsToOrders(rows: Row[]): Order[] {
  const map = new Map<string, (Order & { _accProfit: number })>();

  for (const r of rows) {
    if (!map.has(r.billNo)) {
      const discount = r.discountAmount ?? 0;
      const after =
        r.afterDiscount != null ? r.afterDiscount : Math.max(0, (r.orderTotal ?? 0) - discount);

      map.set(r.billNo, {
        billNo: r.billNo,
        paymentMethod: r.paymentMethod ?? null,
        saleChannel: r.saleChannel ?? null,
        recDateBE: r.recDateBE ?? null,
        recTime: r.recTime ?? null,
        totalItems: r.totalItems ?? 0,
        orderTotal: r.orderTotal ?? 0,
        packageFee: r.packageFee ?? 0,
        shippingFee: r.shippingFee ?? 0,
        discountAmount: discount,
        afterDiscount: after,
        createdAt: r.orderCreated,
        items: [],
        billProfit: 0,
        _accProfit: 0,
      });
    }

    if (r.productCode || r.productName) {
      const qty = Number(r.qty ?? 0);
      const price = Number(r.unitPrice ?? 0);
      const tiktokNetPayout = r.tiktokNetPayout == null ? null : Number(r.tiktokNetPayout);
      const effectiveUnitPrice =
        tiktokNetPayout != null && Number.isFinite(tiktokNetPayout) && tiktokNetPayout > 0
          ? tiktokNetPayout
          : price;
      const cost = Number(r.unitCost ?? 0);
      const lineProfit = qty * (effectiveUnitPrice - cost);

      const o = map.get(r.billNo)!;
      o.items.push({
        productCode: r.productCode ?? "-",
        productName: r.productName ?? "-",
        qty,
        unitPrice: price,
        effectiveUnitPrice,
        unitCost: cost,
        lineTotal: qty * effectiveUnitPrice,
        lineProfit,
      });
      // KC: items เก็บไว้ใน o.items เพื่อให้ useMemo keychain อ่านได้ แต่ไม่นับเข้า billProfit ปกติ
      if (!isKeychainCode(r.productCode ?? "")) {
        o._accProfit += lineProfit;
      }
    }
  }

  const orders: Order[] = Array.from(map.values()).map(({ _accProfit, ...o }) => ({
    ...o,
    billProfit:
      (_accProfit ?? 0) -
      (o.discountAmount ?? 0) -
      (o.saleChannel === "TIKTOK_SHOP" ? (o.packageFee ?? 0) + (o.shippingFee ?? 0) : 0),
  }));

  return orders;
}

/** ---------- [NEW] แยกกำไรแม่เหล็ก / ไม่ใช่แม่เหล็ก ---------- */

// PMN-CRITICAL(/OrdersProfitSummaryPage): Magnet reporting is hardcoded to these product codes; changing PD350/PD351 requires PMN and report updates.
const MAGNET_CODES = ["PD350", "PD351"];

// keychain_options items บันทึกด้วย product_code รูปแบบ KC:{id} — ต้องแยกออกจากสินค้าปกติ
const isKeychainCode = (code: string) => code.startsWith("KC:");

/** ---------- [ADD] เงินสด/สแกน: normalize ให้ชัวร์ ---------- */
function normalizeMethod(method: string | null): string {
  return (method ?? "").trim().toLowerCase();
}

const CASH_METHODS = new Set(["cash", "เงินสด"]);
const SCAN_METHODS = new Set(["scan", "สแกน", "qr", "promptpay", "พร้อมเพย์"]);

function isCashPayment(method: string | null): boolean {
  const m = normalizeMethod(method);
  return CASH_METHODS.has(m);
}

function isScanPayment(method: string | null): boolean {
  const m = normalizeMethod(method);
  return SCAN_METHODS.has(m);
}

function countMagnetQty(o: Order) {
  let big = 0; // PD350
  let small = 0; // PD351

  for (const it of o.items) {
    if (it.productCode === "PD350") big += it.qty;
    if (it.productCode === "PD351") small += it.qty;
  }

  return { big, small, total: big + small };
}

function countNonMagnetQty(o: Order) {
  let total = 0;

  for (const it of o.items) {
    if (!MAGNET_CODES.includes(it.productCode) && !isKeychainCode(it.productCode)) {
      total += it.qty;
    }
  }

  return total;
}

function splitProfitWithDiscount(o: Order): { magnet: number; nonMagnet: number } {
  // PMN-CRITICAL(/OrdersProfitSummaryPage): Discount is split between magnet/non-magnet profit here, so changing this changes dashboard business numbers.
  const profitMagnet = o.items
    .filter((it) => MAGNET_CODES.includes(it.productCode))
    .reduce((sum, it) => sum + it.lineProfit, 0);

  const profitNonMagnet = o.items
    .filter((it) => !MAGNET_CODES.includes(it.productCode) && !isKeychainCode(it.productCode))
    .reduce((sum, it) => sum + it.lineProfit, 0);

  const totalProfitBeforeDiscount = profitMagnet + profitNonMagnet;
  const discount = o.discountAmount ?? 0;

  // ไม่มีส่วนลดหรือกำไรรวม = 0 → ไม่ต้องกระจายซับซ้อน
  if (discount <= 0 || totalProfitBeforeDiscount === 0) {
    return {
      magnet: profitMagnet,
      nonMagnet: profitNonMagnet - discount,
    };
  }

  // กระจายส่วนลดตามสัดส่วนกำไรก่อนลด
  const magnetShare = (discount * profitMagnet) / totalProfitBeforeDiscount;
  const nonMagnetShare = discount - magnetShare;

  return {
    magnet: profitMagnet - magnetShare,
    nonMagnet: profitNonMagnet - nonMagnetShare,
  };
}

/** ---------- คำนวณกรอบเวลา (Asia/Bangkok) ---------- */
function startOfToday(): Date {
  const now = new Date();
  return startOfDay(now);
}
function endOfToday(): Date {
  const now = new Date();
  return endOfDay(now);
}
function addDays(d: Date, days: number): Date {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + days);
  return x;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0);
}

/** ---------- หน้า Summary ---------- */
export default function OrdersProfitSummaryPage(): JSX.Element {
  const [raw, setRaw] = useState<Row[]>([]);
  const [saleChannels, setSaleChannels] = useState<SaleChannelOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<string>("");
  const [saleChannelFilter, setSaleChannelFilter] = useState<string>("");

  // ช่วงคงที่: วันนี้ / เดือนนี้
  const todayStart = useMemo<Date>(() => startOfToday(), []);
  const todayEnd = useMemo<Date>(() => endOfToday(), []);
  const monthStart = useMemo<Date>(() => startOfMonth(new Date()), []);

  // ช่วงปรับเอง (แทน 3 วันล่าสุด)
  const [customFrom, setCustomFrom] = useState<Date | null>(addDays(startOfToday(), -2));
  const [customTo, setCustomTo] = useState<Date | null>(endOfDay(new Date()));

  // KPI: ช่วงที่ผู้ใช้เลือกเอง
  const customRange = useMemo<{ from: Date | null; to: Date | null }>(() => {
    const from = customFrom ? startOfDay(customFrom) : null;
    const to = customTo ? endOfDay(customTo) : null;
    return { from, to };
  }, [customFrom, customTo]);

  // ===== Local state (ย้ายเข้ามาใน component เพื่อแก้ปัญหา customRange / hooks) =====
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [settlementErr, setSettlementErr] = useState<string | null>(null);
  const [settlement, setSettlement] = useState<SettlementItem | null>(null);

  const [countedCash, setCountedCash] = useState<string>(""); // เก็บเป็น string กันพิมพ์
  const [countedScan, setCountedScan] = useState<string>("");

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState<string | null>(null);
  const [history, setHistory] = useState<SettlementItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);



  async function loadSettlementHistory(): Promise<void> {
    setHistoryLoading(true);
    setHistoryErr(null);

    try {
      const resp = await fetch(apiUrl("/settlements/history?limit=20"));
      const data = await safeJson<SettlementGetRespApi>(resp);

      if (!resp.ok) {
        setHistoryErr((data as any)?.error ?? "load_history_failed");
        setHistory([]);
        return;
      }

      const mapped = (data.items ?? []).map(mapSettlementRow);
      setHistory(mapped);
      setShowHistory(true);
    } catch {
      setHistoryErr("load_history_failed");
    } finally {
      setHistoryLoading(false);
    }
  }

  async function safeJson<T>(resp: Response): Promise<T> {
    const t = await resp.text();
    return (t ? JSON.parse(t) : {}) as T;
  }

  async function loadSettlementRange(df: string, dt: string): Promise<void> {
  setSettlementLoading(true);
  setSettlementErr(null);

  try {
    const url = apiUrl(
      `/settlements/range?date_from=${encodeURIComponent(df)}&date_to=${encodeURIComponent(dt)}`
    );

    const resp = await fetch(url);
    const data = await safeJson<SettlementGetRespApi>(resp);

    if (!resp.ok) {
      setSettlement(null);
      setCountedCash("");
      setCountedScan("");
      setSettlementErr((data as any)?.error ?? "load_settlement_failed");
      return;
    }

    const rowApi = Array.isArray(data.items) && data.items.length > 0 ? data.items[0] : null;
    const row = rowApi ? mapSettlementRow(rowApi) : null;

    setSettlement(row);
    setCountedCash(row?.countedCash != null ? String(row.countedCash) : "");
    setCountedScan(row?.countedScan != null ? String(row.countedScan) : "");
  } catch {
    setSettlementErr("load_settlement_failed");
  } finally {
    setSettlementLoading(false);
  }
}

  async function saveSettlement(
    df: string,
    dt: string,
    channel: "CASH" | "SCAN",
    salesAmount: number,
    countedAmountStr: string
  ): Promise<void> {
    // PMN-CRITICAL(/OrdersProfitSummaryPage): salesAmount is the frontend-calculated cash/scan total for the current custom range and saleChannelFilter.
    setSettlementLoading(true);
    setSettlementErr(null);
    try {
      const countedAmount = Number(countedAmountStr);
      if (!Number.isFinite(countedAmount) || countedAmount < 0) {
        setSettlementErr("กรอกยอดนับเงินให้ถูกต้อง");
        return;
      }

      const body: SettlementPutReq = {
        dateFrom: df,
        dateTo: dt,
        channel,
        salesAmount: Number(salesAmount || 0),
        countedAmount,
      };

      const resp = await fetch(apiUrl("/settlements/range"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await safeJson<SettlementPutResp & { error?: string }>(resp);
      if (!resp.ok) {
        setSettlementErr(data?.error ?? "save_settlement_failed");
        return;
      }

      // reload เพื่อให้หน้าสะท้อนค่าจาก DB ล่าสุด
      await loadSettlementRange(df, dt);
    } catch (e) {
      setSettlementErr("save_settlement_failed");
    } finally {
      setSettlementLoading(false);
    }
  }

  // ===== effect: โหลด settlement ทุกครั้งที่ผู้ใช้เปลี่ยนช่วงวัน =====
  const settlementDf = useMemo(() => (customRange.from ? toYMDLocal(customRange.from) : ""), [customRange.from]);

  const settlementDt = useMemo(() => (customRange.to ? toYMDLocal(customRange.to) : ""), [customRange.to]);

  function formatThaiDateOnlyFromIso(iso: string | null | undefined): string {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";

    return d.toLocaleDateString("th-TH", {
      timeZone: "Asia/Bangkok",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  function formatThaiRange(df: string | null | undefined, dt: string | null | undefined): string {
    const a = formatThaiDateOnlyFromIso(df);
    const b = formatThaiDateOnlyFromIso(dt);
    return `${a} → ${b}`;
  }

  useEffect(() => {
    if (!settlementDf || !settlementDt) return;
    void loadSettlementRange(settlementDf, settlementDt);
  }, [settlementDf, settlementDt]);
  // ===== end settlement block =====

  const load = async (): Promise<void> => {
    // PMN-CRITICAL(/OrdersProfitSummaryPage): Both orders-summary and sale-channels must load; sale-channels requires auth and currently gates the whole dashboard refresh.
    setLoading(true);
    setErr("");
    try {
      const token = localStorage.getItem("bg_shop_token") ?? "";
      const [ordersResp, saleChannelsResp] = await Promise.all([
        fetch(apiUrl("/orders-summary")),
        fetch(apiUrl("/sale-channels"), {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        }),
      ]);

      const ordersJson = await ordersResp.json().catch(() => ({}));
      const saleChannelsJson = await saleChannelsResp.json().catch(() => ({}));

      if (!ordersResp.ok) {
        throw new Error(ordersJson?.error ?? ordersResp.statusText);
      }
      if (!saleChannelsResp.ok) {
        throw new Error(saleChannelsJson?.error ?? saleChannelsResp.statusText);
      }

      const items: Row[] = Array.isArray(ordersJson?.items) ? ordersJson.items : [];
      const channelItems: SaleChannelOption[] = Array.isArray(saleChannelsJson?.items)
        ? saleChannelsJson.items
            .map((row: any) => {
              const id = Number(row?.id);
              const code = typeof row?.code === "string" ? row.code.trim() : "";
              const label = typeof row?.label === "string" ? row.label.trim() : "";
              if (!Number.isInteger(id) || id <= 0 || !code || !label) return null;
              return {
                id,
                code,
                label,
                is_active: Boolean(row?.is_active),
              } as SaleChannelOption;
            })
            .filter((row: SaleChannelOption | null): row is SaleChannelOption => row != null)
        : [];

      setRaw(items);
      setSaleChannels(channelItems);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "โหลดข้อมูลไม่สำเร็จ";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const orders = useMemo<Order[]>(() => rowsToOrders(raw), [raw]);
  const saleChannelOptions = useMemo<string[]>(
    () =>
      saleChannels
        .filter((channel) => channel.is_active)
        .map((channel) => channel.code),
    [saleChannels]
  );
  const filteredOrders = useMemo<Order[]>(
    () =>
      saleChannelFilter
        ? orders.filter((order) => order.saleChannel === saleChannelFilter)
        : orders,
    [orders, saleChannelFilter]
  );

  // KPI: วันนี้
  const profitToday = useMemo<number>(() => {
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt) return sum;
      return dt >= todayStart && dt <= todayEnd ? sum + o.billProfit : sum;
    }, 0);
  }, [filteredOrders, todayStart, todayEnd]);

  const billsToday = useMemo<number>(() => {
    return filteredOrders.reduce((c, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < todayStart || dt > todayEnd) return c;
      if (o.items.some((it) => isKeychainCode(it.productCode))) return c;
      return c + 1;
    }, 0);
  }, [filteredOrders, todayStart, todayEnd]);

  const itemsToday = useMemo<number>(() => {
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < todayStart || dt > todayEnd) return sum;
      return sum + o.items.filter((it) => !isKeychainCode(it.productCode)).reduce((s, it) => s + it.qty, 0);
    }, 0);
  }, [filteredOrders, todayStart, todayEnd]);

  const keychainProfitToday = useMemo<number>(() => {
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < todayStart || dt > todayEnd) return sum;
      return sum + o.items.filter((it) => isKeychainCode(it.productCode)).reduce((s, it) => s + it.lineProfit, 0);
    }, 0);
  }, [filteredOrders, todayStart, todayEnd]);

  const keychainBillsToday = useMemo<number>(() => {
    return filteredOrders.reduce((c, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < todayStart || dt > todayEnd) return c;
      return o.items.some((it) => isKeychainCode(it.productCode)) ? c + 1 : c;
    }, 0);
  }, [filteredOrders, todayStart, todayEnd]);

  // [NEW] นับจำนวนแม่เหล็กเล็ก/ใหญ่ + งานโคเช่ เฉพาะ "วันนี้"
  const todayMagnetAndCrochetQty = useMemo(() => {
    let big = 0; // PD350
    let small = 0; // PD351
    let crochet = 0; // นอกเหนือจาก PD350/PD351

    for (const o of filteredOrders) {
      const dt = resolveOrderDate(o);
      if (!dt || dt < todayStart || dt > todayEnd) continue;

      for (const it of o.items) {
        if (!it.productCode) continue;

        if (it.productCode === "PD350") {
          big += it.qty;
        } else if (it.productCode === "PD351") {
          small += it.qty;
        } else if (!isKeychainCode(it.productCode)) {
          crochet += it.qty;
        }
      }
    }

    return { big, small, crochet };
  }, [filteredOrders, todayStart, todayEnd]);

  const { big: magnetBigToday, small: magnetSmallToday, crochet: crochetToday } = todayMagnetAndCrochetQty;

  const profitCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt) return sum;
      return dt >= from && dt <= to ? sum + o.billProfit : sum;
    }, 0);
  }, [filteredOrders, customRange]);

  /** ---------- [ADD] ยอดขายสุทธิ เงินสด/สแกน (ช่วงวันที่เลือก) ---------- */
  const netCashCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
  
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;
      if (!isCashPayment(o.paymentMethod)) return sum;
      return sum + Number(o.afterDiscount ?? 0);
    }, 0);
  }, [filteredOrders, customRange]);
  
  const netScanCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
  
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;
      if (!isScanPayment(o.paymentMethod)) return sum;
      return sum + Number(o.afterDiscount ?? 0);
    }, 0);
  }, [filteredOrders, customRange]);

  const cashDiff = useMemo<number>(() => {
    return parseMoneyInput(countedCash) - netCashCustomRange;
  }, [countedCash, netCashCustomRange]);
  
  const scanDiff = useMemo<number>(() => {
    return parseMoneyInput(countedScan) - netScanCustomRange;
  }, [countedScan, netScanCustomRange]);

  

  /** ---------- [ADD] จำนวนบิล/ชิ้น เงินสด/สแกน (ช่วงวันที่เลือก) ---------- */
  const billsCashCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;

    return filteredOrders.reduce((count, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return count;
      if (!isCashPayment(o.paymentMethod)) return count;
      return count + 1;
    }, 0);
  }, [filteredOrders, customRange]);

  const itemsCashCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;

    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;
      if (!isCashPayment(o.paymentMethod)) return sum;
      return sum + Number(o.totalItems ?? 0);
    }, 0);
  }, [filteredOrders, customRange]);

  const billsScanCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;

    return filteredOrders.reduce((count, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return count;
      if (!isScanPayment(o.paymentMethod)) return count;
      return count + 1;
    }, 0);
  }, [filteredOrders, customRange]);

  const itemsScanCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;

    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;
      if (!isScanPayment(o.paymentMethod)) return sum;
      return sum + Number(o.totalItems ?? 0);
    }, 0);
  }, [filteredOrders, customRange]);

  // [NEW] กำไรช่วงวันที่เลือก เฉพาะแม่เหล็ก (หลังหักส่วนลดแล้ว)
  const profitOnlyMagnetCustom = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;

    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;

      const { magnet } = splitProfitWithDiscount(o);
      return sum + magnet;
    }, 0);
  }, [filteredOrders, customRange]);

  const magnetQtyCustom = useMemo(() => {
    const { from, to } = customRange;
    let big = 0;
    let small = 0;

    if (!from || !to) return { big: 0, small: 0, total: 0 };

    for (const o of filteredOrders) {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) continue;

      const c = countMagnetQty(o);
      big += c.big;
      small += c.small;
    }

    return { big, small, total: big + small };
  }, [filteredOrders, customRange]);

  // [NEW] กำไรช่วงวันที่เลือก ไม่รวมแม่เหล็ก (หลังหักส่วนลดแล้ว)
  const profitExcludeMagnetCustom = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;

    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;

      const { nonMagnet } = splitProfitWithDiscount(o);
      return sum + nonMagnet;
    }, 0);
  }, [filteredOrders, customRange]);

  const nonMagnetQtyCustom = useMemo(() => {
    const { from, to } = customRange;
    let total = 0;

    if (!from || !to) return 0;

    for (const o of filteredOrders) {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) continue;

      total += countNonMagnetQty(o);
    }

    return total;
  }, [filteredOrders, customRange]);

  // [ADD] จำนวนบิล/ชิ้นในช่วงที่เลือก
  const billsCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
    return filteredOrders.reduce((count, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return count;
      if (o.items.some((it) => isKeychainCode(it.productCode))) return count; // KC bill → ไม่นับ
      return count + 1;
    }, 0);
  }, [filteredOrders, customRange]);

  const itemsCustomRange = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;
      return sum + o.items.filter((it) => !isKeychainCode(it.productCode)).reduce((s, it) => s + it.qty, 0);
    }, 0);
  }, [filteredOrders, customRange]);

  const keychainBillsCustom = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
    return filteredOrders.reduce((count, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return count;
      return o.items.some((it) => isKeychainCode(it.productCode)) ? count + 1 : count;
    }, 0);
  }, [filteredOrders, customRange]);

  // KPI: keychain_options items (ช่วงวันที่เลือก) — product_code ขึ้นต้น KC:
  const keychainSalesCustom = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;
      return sum + o.items.filter((it) => isKeychainCode(it.productCode)).reduce((s, it) => s + it.lineTotal, 0);
    }, 0);
  }, [filteredOrders, customRange]);

  const keychainProfitCustom = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;
      return sum + o.items.filter((it) => isKeychainCode(it.productCode)).reduce((s, it) => s + it.lineProfit, 0);
    }, 0);
  }, [filteredOrders, customRange]);

  const keychainQtyCustom = useMemo<number>(() => {
    const { from, to } = customRange;
    if (!from || !to) return 0;
    return filteredOrders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < from || dt > to) return sum;
      return sum + o.items.filter((it) => isKeychainCode(it.productCode)).reduce((s, it) => s + it.qty, 0);
    }, 0);
  }, [filteredOrders, customRange]);

  // KPI: เดือนนี้ (Month-to-date)
  // PMN-CRITICAL(/OrdersProfitSummaryPage): Month KPI cards currently use all orders, not filteredOrders, so saleChannelFilter does not affect these cards.
  const profitThisMonth = useMemo<number>(() => {
    return orders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt) return sum;
      return dt >= monthStart && dt <= todayEnd ? sum + o.billProfit : sum;
    }, 0);
  }, [orders, monthStart, todayEnd]);

  const billsThisMonth = useMemo<number>(() => {
    return orders.reduce((count, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < monthStart || dt > todayEnd) return count;
      if (o.items.some((it) => isKeychainCode(it.productCode))) return count;
      return count + 1;
    }, 0);
  }, [orders, monthStart, todayEnd]);

  const itemsThisMonth = useMemo<number>(() => {
    return orders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < monthStart || dt > todayEnd) return sum;
      return sum + o.items.filter((it) => !isKeychainCode(it.productCode)).reduce((s, it) => s + it.qty, 0);
    }, 0);
  }, [orders, monthStart, todayEnd]);

  const keychainProfitThisMonth = useMemo<number>(() => {
    return orders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < monthStart || dt > todayEnd) return sum;
      return sum + o.items.filter((it) => isKeychainCode(it.productCode)).reduce((s, it) => s + it.lineProfit, 0);
    }, 0);
  }, [orders, monthStart, todayEnd]);

  const keychainBillsThisMonth = useMemo<number>(() => {
    return orders.reduce((count, o) => {
      const dt = resolveOrderDate(o);
      if (!dt || dt < monthStart || dt > todayEnd) return count;
      return o.items.some((it) => isKeychainCode(it.productCode)) ? count + 1 : count;
    }, 0);
  }, [orders, monthStart, todayEnd]);

  // รวม (ยอดสุทธิ) และ (ส่วนลด) ต่อเดือน (MTD)
  const netThisMonth = useMemo<number>(() => {
    return orders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt) return sum;
      return dt >= monthStart && dt <= todayEnd ? sum + (o.afterDiscount ?? 0) : sum;
    }, 0);
  }, [orders, monthStart, todayEnd]);

  const discountThisMonth = useMemo<number>(() => {
    return orders.reduce((sum, o) => {
      const dt = resolveOrderDate(o);
      if (!dt) return sum;
      return dt >= monthStart && dt <= todayEnd ? sum + (o.discountAmount ?? 0) : sum;
    }, 0);
  }, [orders, monthStart, todayEnd]);

  // [ADD] เปอร์เซ็นต์เทียบยอดสุทธิรวมของเดือนนี้
  const profitPctOfNetThisMonth = useMemo<number>(() => {
    return netThisMonth > 0 ? (profitThisMonth / netThisMonth) * 100 : 0;
  }, [profitThisMonth, netThisMonth]);

  const discountPctOfNetThisMonth = useMemo<number>(() => {
    return netThisMonth > 0 ? (discountThisMonth / netThisMonth) * 100 : 0;
  }, [discountThisMonth, netThisMonth]);

  
function parseMoneyInput(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function diffClassName(diff: number): string {
  if (diff > 0) return "text-success fw-semibold";
  if (diff < 0) return "text-danger fw-semibold";
  return " fw-semibold";
}

function diffLabel(diff: number): string {
  if (diff > 0) return "เกิน";
  if (diff < 0) return "ขาด";
  return "ตรง";
}



  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0 text-primary">สรุปกำไร (วันนี้ / ช่วงที่เลือก / เดือนนี้)</h4>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => void load()}
                  disabled={loading}
                  title="รีเฟรชข้อมูล"
                >
                  {loading ? "กำลังโหลด…" : "รีเฟรช"}
                </button>
                <a href="/" className="btn btn-link text-decoration-none">
                  ← กลับหน้าเมนู
                </a>
              </div>
            </div>

            {err && (
              <div className="alert alert-warning rounded-3" role="alert">
                {err}
              </div>
            )}

            {/* Range สำหรับ (ช่วงที่เลือกเอง) */}
            <div className="mb-3 d-flex flex-column flex-lg-row justify-content-center align-items-center gap-2">
              <DateRange from={customFrom} to={customTo} onChangeFrom={setCustomFrom} onChangeTo={setCustomTo} />
              <select
                className="form-select"
                style={{ maxWidth: 220 }}
                value={saleChannelFilter}
                onChange={(e) => setSaleChannelFilter(e.target.value)}
              >
                <option value="">ทุกสถานที่ขาย</option>
                {saleChannelOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            {/* ===== Settlement (นับเงิน) ===== */}
            <details className="card shadow border-0 rounded-4 mb-3">
              <summary
                className="card-body d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", listStyle: "none" }}
              >
                <div>
                  <div className="fw-semibold">บันทึกการนับเงิน (ตามช่วงวันที่เลือก)</div>
                  <div className="text-muted small">
                    {settlementDf || "-"} → {settlementDt || "-"}
                    <div className="text-muted small d-flex flex-wrap gap-2 align-items-center">
                      

                      {settlement?.updatedAt && (
                        <span className="fw-semibold text-success" style={{ fontSize: "0.95rem" }}>
                          ✔ อัปเดตล่าสุด {formatThaiDateTime(settlement.updatedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                
                  
                  <button
                    className="btn btn-outline-primary btn-sm"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();   // กัน summary toggle
                      e.stopPropagation();  // กัน event ไหลไป summary
                      void loadSettlementHistory();
                    }}
                  >
                    ดูประวัติ
                  </button>

                  {settlementLoading && <div className="text-muted small">กำลังโหลด/บันทึก…</div>}
                  <span className="text-muted">▾</span>

                  
                </div>
              </summary>

              <div className="card-body pt-0">
                {settlementErr && (
                  <div className="alert alert-warning rounded-3 mt-3 mb-0" role="alert">
                    {settlementErr}
                  </div>
                )}

                <div className="row g-3 mt-1">
                  {/* เงินสด */}
                  <div className="col-12 col-md-6">
                    <div className="border rounded-4 p-3 h-100">
                      <div className="fw-semibold mb-1">เงินสด</div>
                      <div className="text-muted small mb-2">
                        ยอดขายตามระบบ: <span className="fw-semibold">{thb(netCashCustomRange)}</span>
                      </div>

                      <label className="form-label small mb-1">ยอดนับเงินจริง</label>
                      <input
                        className="form-control"
                        inputMode="decimal"
                        value={countedCash}
                        onChange={(e) => setCountedCash(e.target.value)}
                        placeholder="เช่น 1234.50"
                        disabled={settlementLoading}
                      />

                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={settlementLoading || !settlementDf || !settlementDt}
                          onClick={() =>
                            void saveSettlement(settlementDf, settlementDt, "CASH", netCashCustomRange, countedCash)
                          }
                        >
                          บันทึกเงินสด
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={settlementLoading || !settlementDf || !settlementDt}
                          onClick={() => void loadSettlementRange(settlementDf, settlementDt)}
                        >
                          โหลดใหม่
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* สแกน */}
                  <div className="col-12 col-md-6">
                    <div className="border rounded-4 p-3 h-100">
                      <div className="fw-semibold mb-1">สแกน/พร้อมเพย์</div>
                      <div className="text-muted small mb-2">
                        ยอดขายตามระบบ: <span className="fw-semibold">{thb(netScanCustomRange)}</span>
                      </div>

                      <label className="form-label small mb-1">ยอดนับจริง (จากสเตทเมนต์)</label>
                      <input
                        className="form-control"
                        inputMode="decimal"
                        value={countedScan}
                        onChange={(e) => setCountedScan(e.target.value)}
                        placeholder="เช่น 5678.00"
                        disabled={settlementLoading}
                      />

                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={settlementLoading || !settlementDf || !settlementDt}
                          onClick={() =>
                            void saveSettlement(settlementDf, settlementDt, "SCAN", netScanCustomRange, countedScan)
                          }
                        >
                          บันทึกสแกน
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={settlementLoading || !settlementDf || !settlementDt}
                          onClick={() => void loadSettlementRange(settlementDf, settlementDt)}
                        >
                          โหลดใหม่
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </details>
            {/* ===== end Settlement ===== */}
            {showHistory && (
            <div
              className="modal fade show"
              style={{ display: "block", background: "rgba(0,0,0,.4)" }}
              onClick={() => setShowHistory(false)} // คลิกฉากหลังปิด
            >
              <div
                className="modal-dialog modal-lg modal-dialog-scrollable"
                onClick={(e) => e.stopPropagation()} // กันคลิกในกล่องแล้วปิด
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">ประวัติการบันทึกการนับเงิน (20 รายการล่าสุด)</h5>
                    <button className="btn-close" onClick={() => setShowHistory(false)} />
                  </div>

                  <div className="modal-body">
                    {historyLoading && <div>กำลังโหลด...</div>}
                    {historyErr && <div className="alert alert-warning">{historyErr}</div>}
                    {!historyLoading && history.length === 0 && <div className="text-muted">ไม่มีข้อมูล</div>}

                    {history.length > 0 && (
                      <table className="table table-sm table-hover align-middle">
                        <thead>
                          <tr>
                            <th>ช่วงวันที่</th>
                            <th className="text-end">ยอดขายเงินสด</th>
                            <th className="text-end">นับเงินจริง</th>
                            <th className="text-end">ส่วนต่างเงินสด</th>
                            <th className="text-end">ยอดขายสแกน</th>
                            <th className="text-end">นับจริงสแกน</th>
                            <th className="text-end">ส่วนต่างสแกน</th>
                            <th className="text-end">ส่วนต่างรวม</th>
                            <th>อัปเดต</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map((h) => {
                            const cashDiff = (h.countedCash ?? 0) - (h.salesCash ?? 0);
                            const scanDiff = (h.countedScan ?? 0) - (h.salesScan ?? 0);
                            const totalDiff = cashDiff + scanDiff;
                            return (
                              <tr key={h.id}>
                                <td>{formatThaiRange(h.dateFrom, h.dateTo)}</td>

                                <td className="text-end">{thb(h.salesCash ?? 0)}</td>

                                <td
                                  className="text-end"
                                  style={{ backgroundColor: "#e8f7ee", fontWeight: 600 }}
                                >
                                  {thb(h.countedCash ?? 0)}
                                </td>

                                <td className="text-end table-secondary">
                                  <span className={diffClassName(cashDiff)}>
                                    {thb(cashDiff)}
                                  </span>
                                </td>

                                <td className="text-end">{thb(h.salesScan ?? 0)}</td>

                                <td
                                  className="text-end"
                                  style={{ backgroundColor: "#e8f7ee", fontWeight: 600 }}
                                >
                                  {thb(h.countedScan ?? 0)}
                                </td>

                                <td className="text-end table-secondary">
                                  <span className={diffClassName(scanDiff)}>
                                    {thb(scanDiff)}
                                  </span>
                                </td>

                                <td
                                  className="text-end"
                                  style={{ backgroundColor: "#495057", fontWeight: 700 }}
                                >
                                  <span className="text-white">
                                    {thb(totalDiff)}
                                  </span>
                                </td>

                                <td>{formatThaiDateTime(h.updatedAt)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowHistory(false)}>
                      ปิด
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

            {/* KPI Cards แถวแรก */}
            <div className="row g-3">
              {/* วันนี้ */}
              <div className="col-12 col-md-4">
                <SummaryCard>
                  <div className="text-muted small">กำไร (วันนี้)</div>
                  <div className="fs-4 fw-bold">{thb(profitToday + keychainProfitToday)}</div>

                  <div className="text-muted small mt-1">
                    {toYMDLocal(todayStart)} • สินค้าปกติ {billsToday} บิล • {itemsToday} ชิ้น
                  </div>
                  {keychainBillsToday > 0 && (
                    <div className="text-muted small">
                      พวงกุญแจ {keychainBillsToday} บิล
                    </div>
                  )}

                  <div className="text-muted small mt-2">
                    แม่เหล็กเล็ก (PD351): <span className="fw-semibold">{magnetSmallToday}</span> ชิ้น
                  </div>
                  <div className="text-muted small">
                    แม่เหล็กใหญ่ (PD350): <span className="fw-semibold">{magnetBigToday}</span> ชิ้น
                  </div>
                  <div className="text-muted small">
                    งานโคเช่ (ไม่ใช่แม่เหล็ก): <span className="fw-semibold">{crochetToday}</span> ชิ้น
                  </div>
                </SummaryCard>
              </div>

              {/* ช่วงที่เลือก */}
              <div className="col-12 col-md-4">
                <div className="card shadow border-0 rounded-4 h-100">
                  <div className="card-body">
                    <div className="text-muted small">กำไร (ช่วงวันที่เลือก)</div>
                    <div className="fs-4 fw-bold">{thb(profitCustomRange + keychainProfitCustom)}</div>
                    <div className="text-muted small mt-1">
                      {(customRange.from && toYMDLocal(customRange.from)) || "-"} →{" "}
                      {(customRange.to && toYMDLocal(customRange.to)) || "-"}
                    </div>
                    <div className="text-muted small mt-1">
                      สินค้าปกติ {billsCustomRange} บิล • {itemsCustomRange} ชิ้น
                    </div>
                    {keychainBillsCustom > 0 && (
                      <div className="text-muted small">
                        พวงกุญแจ {keychainBillsCustom} บิล (ดูกำไรด้านล่าง)
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* [NEW] ช่วงที่เลือก - เฉพาะแม่เหล็ก */}
              <div className="col-12 col-md-4">
                <div className="card shadow border-0 rounded-4 h-100">
                  <div className="card-body">
                    <div className="text-muted small">กำไร (ช่วงวันที่เลือก — เฉพาะแม่เหล็ก)</div>

                    <div className="fs-4 fw-bold">{thb(profitOnlyMagnetCustom)}</div>

                    <div className="text-muted small mt-1">
                      {(customRange.from && toYMDLocal(customRange.from)) || "-"} →{" "}
                      {(customRange.to && toYMDLocal(customRange.to)) || "-"}
                    </div>

                    {/* จำนวนแม่เหล็ก */}
                    <div className="text-muted small mt-2">
                      แม่เหล็กเล็ก (PD351): <span className="fw-semibold">{magnetQtyCustom.small}</span> ชิ้น
                    </div>
                    <div className="text-muted small ">
                      แม่เหล็กใหญ่ (PD350): <span className="fw-semibold">{magnetQtyCustom.big}</span> ชิ้น
                    </div>
                    <div className="text-muted small">
                      รวมแม่เหล็ก: <span className="fw-semibold">{magnetQtyCustom.total}</span> ชิ้น
                    </div>
                  </div>
                </div>
              </div>

              {/* [NEW] ช่วงที่เลือก - ไม่รวมแม่เหล็ก (งานโคเช่) */}
              <div className="col-12 col-md-4">
                <div className="card shadow border-0 rounded-4 h-100">
                  <div className="card-body">
                    <div className="text-muted small">กำไร (ช่วงวันที่เลือก — งานโคเช่)</div>

                    <div className="fs-4 fw-bold">{thb(profitExcludeMagnetCustom)}</div>

                    <div className="text-muted small mt-1">
                      {(customRange.from && toYMDLocal(customRange.from)) || "-"} →{" "}
                      {(customRange.to && toYMDLocal(customRange.to)) || "-"}
                    </div>

                    {/* จำนวนงานโคเช่ */}
                    <div className="text-muted small mt-2">
                      งานโคเช่ทั้งหมด: <span className="fw-semibold">{nonMagnetQtyCustom}</span> ชิ้น
                    </div>
                  </div>
                </div>
              </div>

              {/* [ADD] ช่วงที่เลือก - พวงกุญแจ (keychain_options) */}
              <div className="col-12 col-md-4">
                <div className="card shadow border-0 rounded-4 h-100">
                  <div className="card-body">
                    <div className="text-muted small">ยอดขาย "ช่วงวันที่เลือก — พวงกุญแจ"</div>
                    <div className="fs-4 fw-bold">{thb(keychainSalesCustom)}</div>
                    <div className="text-muted small mt-1">
                      {(customRange.from && toYMDLocal(customRange.from)) || "-"} →{" "}
                      {(customRange.to && toYMDLocal(customRange.to)) || "-"}
                    </div>
                    <div className="text-muted small mt-2">
                      กำไร <span className="fw-semibold">{thb(keychainProfitCustom)}</span> · <span className="fw-semibold">{keychainQtyCustom}</span> ชิ้น
                    </div>
                  </div>
                </div>
              </div>

              {/* [ADD] ช่วงที่เลือก - เฉพาะเงินสด (ยอดขาย) */}
              <div className="col-12 col-md-4">
                <div className="card shadow border-0 rounded-4 h-100">
                  <div className="card-body">
                    <div className="text-muted small">ยอดขาย (ช่วงวันที่เลือก — เฉพาะเงินสด)</div>
                    <div className="fs-4 fw-bold">{thb(netCashCustomRange)}</div>

                    <div className="text-muted small mt-1">
                      {(customRange.from && toYMDLocal(customRange.from)) || "-"} →{" "}
                      {(customRange.to && toYMDLocal(customRange.to)) || "-"}
                    </div>

                    <div className="text-muted small mt-1">
                      บิล {billsCashCustomRange} ใบ • ชิ้น {itemsCashCustomRange} ชิ้น
                    </div>
                  </div>
                </div>
              </div>

              {/* [ADD] ช่วงที่เลือก - เฉพาะสแกน (ยอดขาย) */}
              <div className="col-12 col-md-4">
                <div className="card shadow border-0 rounded-4 h-100">
                  <div className="card-body">
                    <div className="text-muted small">ยอดขาย (ช่วงวันที่เลือก — เฉพาะสแกน)</div>
                    <div className="fs-4 fw-bold">{thb(netScanCustomRange)}</div>

                    <div className="text-muted small mt-1">
                      {(customRange.from && toYMDLocal(customRange.from)) || "-"} →{" "}
                      {(customRange.to && toYMDLocal(customRange.to)) || "-"}
                    </div>

                    <div className="text-muted small mt-1">
                      บิล {billsScanCustomRange} ใบ • ชิ้น {itemsScanCustomRange} ชิ้น
                    </div>
                  </div>
                </div>
              </div>

              {/* เดือนนี้ (เขียว) + เปอร์เซ็นต์จากยอดสุทธิรวม */}
              <div className="col-12 col-md-4">
                <div className="card shadow border-0 rounded-4 h-100 bg-success text-white">
                  <div className="card-body">
                    <div className="small opacity-75">กำไร (เดือนนี้)</div>
                    <div className="fs-4 fw-bold">{thb(profitThisMonth + keychainProfitThisMonth)}</div>
                    <div className="small opacity-75 mt-1">
                      {toYMDLocal(monthStart)} → {toYMDLocal(todayEnd)}
                    </div>
                    <div className="small opacity-75 mt-1">
                      สินค้าปกติ {billsThisMonth} บิล • {itemsThisMonth} ชิ้น
                    </div>
                    {keychainBillsThisMonth > 0 && (
                      <div className="small opacity-75">
                        พวงกุญแจ {keychainBillsThisMonth} บิล
                      </div>
                    )}
                    <div className="small opacity-75 mt-1">
                      คิดเป็น {profitPctOfNetThisMonth.toFixed(1)}% ของยอดสุทธิรวม
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI Cards แถวสอง: ยอดสุทธิรวม & ส่วนลดรวม (ต่อเดือน) */}
            <div className="row g-3 mt-1">
              {/* ยอดสุทธิรวม ต่อเดือน */}
              <div className="col-12 col-md-6">
                <div className="card shadow border-0 rounded-4 h-100">
                  <div className="card-body">
                    <div className="text-muted small">ยอดสุทธิรวม (เดือนนี้)</div>
                    <div className="fs-4 fw-bold">{thb(netThisMonth)}</div>
                    <div className="text-muted small mt-1">
                      {toYMDLocal(monthStart)} → {toYMDLocal(todayEnd)}
                    </div>
                  </div>
                </div>
              </div>

              {/* ส่วนลดรวม ต่อเดือน (แดง) + เปอร์เซ็นต์จากยอดสุทธิรวม */}
              <div className="col-12 col-md-6">
                <div className="card shadow border-0 rounded-4 h-100 text-danger" style={{ backgroundColor: "#fde8e8" }}>
                  <div className="card-body">
                    <div className="small opacity-75">ส่วนลดรวม (เดือนนี้)</div>
                    <div className="fs-4 fw-bold">{thb(discountThisMonth)}</div>
                    <div className="small opacity-75 mt-1">
                      {toYMDLocal(monthStart)} → {toYMDLocal(todayEnd)}
                    </div>
                    <div className="small opacity-75 mt-1">
                      คิดเป็น {discountPctOfNetThisMonth.toFixed(1)}% ของยอดสุทธิรวม
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* บิลล่าสุด (อ้างอิงเร็ว ๆ) */}
            <div className="card shadow border-0 rounded-4 mt-4">
              <div className="card-body">
                <h6 className="mb-3">บิลล่าสุด (100 ใบ)</h6>
                <div
                  className="table-responsive"
                  style={{
                    maxHeight: "480px",
                    overflowY: "auto",
                    borderRadius: "0.5rem",
                  }}
                >
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light position-sticky top-0">
                      <tr>
                        <th style={{ width: 120 }}>เลขบิล</th>
                        <th style={{ width: 180 }}>วันที่</th>
                        <th className="text-end" style={{ width: 160 }}>
                          ยอดสุทธิ
                        </th>
                        <th className="text-end" style={{ width: 160 }}>
                          กำไรต่อบิล
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders
                        .slice()
                        .sort((a, b) => {
                          const ad = resolveOrderDate(a)?.getTime() ?? 0;
                          const bd = resolveOrderDate(b)?.getTime() ?? 0;
                          if (ad !== bd) return bd - ad;
                          return b.billNo.localeCompare(a.billNo, undefined, {
                            numeric: true,
                            sensitivity: "base",
                          });
                        })
                        .slice(0, 100)
                        .map((o) => (
                          <tr key={o.billNo}>
                            <td className="fw-semibold text-center">#{o.billNo}</td>
                            <td className="text-nowrap">{toYMDLocal(resolveOrderDate(o) ?? new Date())}</td>
                            <td className="text-end">{thb(o.afterDiscount)}</td>
                            <td className="text-end">
                              <span className={o.billProfit >= 0 ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                                {thb(o.billProfit)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      {!loading && orders.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            ไม่พบบิล
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * ใช้กฎเดียวกับ OrdersProfitPage: map API → UI types, แยก helper, ไม่เก็บค่าที่คำนวณซ้ำ
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
